import React from 'react'
import clsx from 'clsx'
import { MdDragHandle } from 'react-icons/md'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { useDrag, useDrop } from 'react-dnd'
import {
  CloseCircle,
  ArrowRight2,
  ArrowDown2,
  ExportSquare
} from 'iconsax-react'
import { useTheme } from '@mui/material'

import { ObjectType } from '../../../types'

import Tooltip from '@SonarCX/components/atoms/Tooltip'
import FieldIcon from '../../../FieldIcon'
import FlowFieldOptions from '../FlowFieldOptions'

import { selectCustomFields } from '@SonarCX/slices/customFields'
import { useFlowTarget } from '../providers/FlowTargetProvider'
import useBaseStyles from '../../GroupSettings/GroupField/styles'
import useStyles from './styles'

type FlowFieldProps = {
  fieldID: string
  isFirst: boolean
  isLast: boolean
  onDelete: () => void
  flow: APIFlow
  onMove: (source: string, target: string, above: boolean) => void
  tab: ObjectType
}

const FlowField: React.FC<FlowFieldProps> = ({
  fieldID,
  isFirst,
  isLast,
  onDelete,
  flow,
  onMove,
  tab
}) => {
  const theme = useTheme()

  const { customFields } = useAppSelector(selectCustomFields)

  const { setTarget } = useFlowTarget()

  const [expanded, setExpanded] = React.useState(false)

  const field = React.useMemo(
    () => customFields.find(field => field.id === fieldID),
    [customFields, fieldID]
  )

  const ITEM_TYPE = `${flow.id}-custom-field`

  const [, dragRef, dragPreviewRef] = useDrag({
    item: { type: ITEM_TYPE, id: fieldID }
  })

  const [{ isOver: isOverTop }, dropTopRef] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: any) => onMove(item.id, fieldID, true),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [{ isOver: isOverBottom }, dropBottomRef] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: any) => onMove(item.id, fieldID, false),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const baseClasses = useBaseStyles()
  const classes = useStyles()

  const viewStage = React.useCallback(() => {
    if (field.field_type === 'flow' && field.config?.id !== null) {
      setTarget(field.config.id)
    }
  }, [setTarget, field])

  return (
    <>
      <div
        ref={dropTopRef}
        className={clsx(
          baseClasses.dropZone,
          isOverTop &&
            (isFirst ? baseClasses.dropZoneTop : baseClasses.dropZoneMiddle)
        )}
      />

      <div className={baseClasses.root}>
        <div ref={dragPreviewRef} className={baseClasses.dragPreview}>
          <div ref={dragRef} className={baseClasses.dragHandle}>
            <MdDragHandle className={baseClasses.dragIcon} />
          </div>

          <div className={baseClasses.name}>
            {field ? (
              <>
                {/* Flows cannot be configured */}
                {field.field_type !== 'flow' ? (
                  field.field_type === 'table' ? null : (
                    <div
                      onClick={() => setExpanded(old => !old)}
                      className={classes.checkbox}
                    >
                      {expanded ? (
                        <ArrowDown2 variant='Bold' size={theme.size.base} />
                      ) : (
                        <ArrowRight2 variant='Bold' size={theme.size.base} />
                      )}
                    </div>
                  )
                ) : (
                  <div className={classes.checkbox}>
                    <ExportSquare
                      onClick={viewStage}
                      size={theme.size.base}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                )}

                <FieldIcon
                  type={field.field_type}
                  size={theme.size.base}
                  className={baseClasses.fieldIcon}
                />

                <Tooltip title={field.description ?? field.title}>
                  <span>{field.title}</span>
                </Tooltip>
              </>
            ) : (
              <span style={{ color: 'gainsboro' }}>Unknown Field</span>
            )}
          </div>
        </div>

        <CloseCircle
          size={theme.size.base}
          onClick={onDelete}
          className={baseClasses.closeIcon}
        />
      </div>

      {expanded && <FlowFieldOptions flow={flow} field={field} tab={tab} />}

      {isLast && (
        <div
          ref={dropBottomRef}
          className={clsx(
            baseClasses.dropZone,
            isOverBottom && baseClasses.dropZoneBottom
          )}
        />
      )}
    </>
  )
}

export default FlowField
